import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useUser } from "../../context/UserContext";
import axios from "../../utils/axiosConfig";
import "../../styles/features/user/Profile.css";

export default function Profile() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { getAccessTokenSilently, logout } = useAuth0();
  const { user: currentUser, setUser } = useUser();

  // Sidebar tabs
  const [tab, setTab] = useState("overview");

  // Dane zaproszeń i wydarzeń
  const [invites, setInvites] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [registeredEvents, setRegisteredEvents] = useState([]);

  const [loadingInvites, setLoadingInvites] = useState(false);
  const [loadingMyEvents, setLoadingMyEvents] = useState(false);
  const [loadingRegisteredEvents, setLoadingRegisteredEvents] = useState(false);

  // Komunikaty
  const [msg, setMsg] = useState("");

  // Formularze ustawień
  const [showDelete, setShowDelete] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  // User z localStorage / API
  const user = useMemo(() => {
    const fallback =
      JSON.parse(localStorage.getItem("user") || "null") || {};

    const source = currentUser || fallback;

    return {
      id: userId || source.id || "me",
      name: source.firstname
        ? `${source.firstname} ${source.surname || ""}`.trim()
        : "Użytkownik soMeet",
      email: source.email || "user@example.com",
      avatar: source.avatar || "/images/girl.jpg",
    };
  }, [userId, currentUser]);

  // Najpierw helper do auth
  const getAuthHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently({
      authorizationParams: {
        audience: "https://someet-api",
      },
    });

    return {
      Authorization: `Bearer ${token}`,
    };
  }, [getAccessTokenSilently]);

  const formatDateTime = (value) => {
    if (!value) return "";

    return new Date(value).toLocaleString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Zaproszenia oczekujące
  const loadInvites = useCallback(async () => {
    try {
      setLoadingInvites(true);

      const headers = await getAuthHeaders();

      const { data } = await axios.get(
        "/me/invitations",
        { headers }
      );

      setInvites(data || []);
    } catch (err) {
      console.error("Nie udało się pobrać zaproszeń:", err);
      setMsg("Nie udało się pobrać zaproszeń.");
      setInvites([]);
    } finally {
      setLoadingInvites(false);
    }
  }, [getAuthHeaders]);

  // Eventy wynikające z zaakceptowanych zaproszeń
  const loadMyEvents = useCallback(async () => {
    try {
      setLoadingMyEvents(true);

      const headers = await getAuthHeaders();

      const { data } = await axios.get(
        "/me/events",
        { headers }
      );

      setMyEvents(data || []);
    } catch (err) {
      console.error("Nie udało się pobrać wydarzeń z zaproszeń:", err);
      setMsg("Nie udało się pobrać wydarzeń.");
      setMyEvents([]);
    } finally {
      setLoadingMyEvents(false);
    }
  }, [getAuthHeaders]);

  // Eventy, na które user zapisał się samodzielnie
const loadRegisteredEvents = useCallback(async () => {
  try {
    setLoadingRegisteredEvents(true);

    const headers = await getAuthHeaders();

    const { data } = await axios.get(
      "/api/users/me/events",
      { headers }
    );

    setRegisteredEvents(data || []);
  } catch (err) {
    console.error(
      "Nie udało się pobrać bezpośrednich zapisów:",
      err
    );

    setRegisteredEvents([]);
  } finally {
    setLoadingRegisteredEvents(false);
  }
}, [getAuthHeaders]);

  useEffect(() => {
    loadInvites();
    loadMyEvents();
    loadRegisteredEvents();
  }, [
    loadInvites,
    loadMyEvents,
    loadRegisteredEvents,
  ]);

  const acceptInvite = async (id) => {
    try {
      const headers = await getAuthHeaders();

      await axios.post(
        `/me/invitations/${id}/accept`,
        {},
        { headers }
      );

      setMsg(
        "Zaproszenie zaakceptowane. Wyślemy Ci e-mail z informacją o płatności."
      );

      await loadInvites();
      await loadMyEvents();
    } catch (err) {
      setMsg(
        err.response?.data?.message ||
          "Nie udało się zaakceptować zaproszenia."
      );
    }
  };

  const declineInvite = async (id) => {
    try {
      const headers = await getAuthHeaders();

      await axios.post(
        `/me/invitations/${id}/decline`,
        {},
        { headers }
      );

      setMsg("Zaproszenie zostało odrzucone.");

      await loadInvites();
      await loadMyEvents();
    } catch (err) {
      setMsg(
        err.response?.data?.message ||
          "Nie udało się odrzucić zaproszenia."
      );
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmText.trim().toUpperCase() !== "USUN") {
      setMsg("Aby potwierdzić, wpisz słowo „USUN”.");
      return;
    }

    try {
      setDeleting(true);

      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://someet-api",
        },
      });

      await axios.delete("/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setUser(null);
      localStorage.removeItem("user");

      logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });

      navigate("/");
    } catch (err) {
      const text =
        err.response?.data?.message ||
        err.response?.data ||
        "Nie udało się usunąć konta.";

      setMsg(String(text));
    } finally {
      setDeleting(false);
      setShowDelete(false);
      setConfirmText("");
    }
  };

  return (
    <section className="profile-page">
      <div className="profile-container">

        {/* HEADER */}
        <header className="profile-header">
          <div className="ph-left">
            <div className="avatar-ring" />

            <div>
              <h1 className="ph-name">{user.name}</h1>
              <div className="ph-email">{user.email}</div>
            </div>
          </div>
        </header>

        <div className="profile-grid">

          {/* SIDEBAR */}
          <aside className="profile-sidebar">

            <button
              className={`side-link ${
                tab === "overview" ? "active" : ""
              }`}
              onClick={() => setTab("overview")}
            >
              <img
                src="/icons/home.png"
                alt=""
                width="24"
                height="24"
              />
              <span>Przegląd</span>
            </button>

            <button
              className={`side-link ${
                tab === "survey" ? "active" : ""
              }`}
              onClick={() => setTab("survey")}
            >
              <img
                src="/icons/survey.png"
                alt=""
                width="24"
                height="24"
              />
              <span>Moja ankieta</span>
            </button>

            <button
              className={`side-link ${
                tab === "invites" ? "active" : ""
              }`}
              onClick={() => setTab("invites")}
            >
              <img
                src="/icons/email.png"
                alt=""
                width="24"
                height="24"
              />
              <span>Zaproszenia</span>
            </button>

            <button
              className={`side-link ${
                tab === "events" ? "active" : ""
              }`}
              onClick={() => setTab("events")}
            >
              <img
                src="/icons/date.png"
                alt=""
                width="24"
                height="24"
              />
              <span>Moje wydarzenia</span>
            </button>

            <button
              className={`side-link ${
                tab === "settings" ? "active" : ""
              }`}
              onClick={() => setTab("settings")}
            >
              <img
                src="/icons/setting.png"
                alt=""
                width="24"
                height="24"
              />
              <span>Ustawienia</span>
            </button>

          </aside>

          {/* MAIN */}
          <main className="profile-main">

            {/* OVERVIEW */}
            {tab === "overview" && (
              <div className="panel">
                <h3>Witaj w swoim profilu 👋</h3>

                <p>
                  Tu szybko przejrzysz zaproszenia,
                  sprawdzisz historię wydarzeń i
                  zaktualizujesz dane.
                </p>

                <div className="quick-cta">
                  <button
                    className="btn btn-primary"
                    onClick={() => setTab("survey")}
                  >
                    Przejdź do ankiety
                  </button>

                  <button
                    className="btn btn-outline"
                    onClick={() => setTab("invites")}
                  >
                    Zobacz zaproszenia
                  </button>
                </div>
              </div>
            )}

            {/* SURVEY */}
            {tab === "survey" && (
              <div className="panel">
                <h3>Moje ankiety</h3>

                <p>
                  Możesz wracać do ankiet i aktualizować
                  odpowiedzi, kiedy tego potrzebujesz.
                  Dzięki temu lepiej dopasujemy wydarzenia
                  i warsztaty do Twoich preferencji.
                </p>

                <div className="survey-choice-grid">

                  <div className="survey-choice-card">
                    <h4>Ankieta dla singli</h4>

                    <p>
                      Ta ankieta jest wymagana do udziału
                      w wydarzeniach dla singli. Dzięki niej
                      możemy lepiej dopasować grupy i
                      zaproszenia do uczestników.
                    </p>

                    <button
                      className="btn btn-primary"
                      onClick={() => navigate("/survey")}
                    >
                      Otwórz ankietę
                    </button>
                  </div>

                  <div className="survey-choice-card">
                    <h4>Ankieta warsztatowa</h4>

                    <p>
                      Ta ankieta jest dobrowolna. Możesz
                      podzielić się swoimi preferencjami
                      i pomysłami, aby pomóc nam tworzyć
                      warsztaty lepiej dopasowane do
                      uczestników.
                    </p>

                    <button
                      className="btn btn-primary"
                      onClick={() =>
                        navigate("/workshop-survey")
                      }
                    >
                      Otwórz ankietę warsztatową
                    </button>
                  </div>

                </div>
              </div>
            )}

            {/* INVITES */}
            {tab === "invites" && (
              <div className="panel">
                <h3>Zaproszenia</h3>

                {msg && (
                  <div className="alert">
                    {msg}
                  </div>
                )}

                {loadingInvites ? (
                  <p>Ładujemy Twoje zaproszenia...</p>
                ) : invites.length === 0 ? (
                  <p>
                    Gdy skompletujemy Twoją grupę,
                    dostaniesz zaproszenie. Będziesz
                    mógł/mogła podejrzeć szczegóły
                    wydarzenia i zaakceptować albo
                    odrzucić.
                  </p>
                ) : (
                  <div className="invite-grid">

                    {invites.map((invite) => (
                      <article
                        className="invite-card"
                        key={invite.id}
                      >

                        <div className="invite-image-wrap">
                          <img
                            src={
                              invite.imageUrl ||
                              invite.img ||
                              "/images/single.jpg"
                            }
                            alt={invite.title}
                            className="invite-image"
                          />
                        </div>

                        <div className="invite-content">

                          <div className="invite-top">
                            <span className="invite-badge">
                              Zaproszenie
                            </span>

                            <span className="invite-deadline">
                              Odpowiedz do:{" "}
                              {formatDateTime(
                                invite.responseDeadline
                              )}
                            </span>
                          </div>

                          <h4>{invite.title}</h4>

                          <div className="invite-meta">
                            <span>{invite.date}</span>
                            <span>{invite.time}</span>
                            <span>
                              {invite.location ||
                                invite.city}
                            </span>
                          </div>

                          <p className="invite-description">
                            Masz zaproszenie do wydarzenia
                            dla singli. Sprawdź szczegóły,
                            a następnie zaakceptuj albo
                            odrzuć udział.
                          </p>

                          <div className="invite-actions">

                            <button
                              className="btn-invite btn-invite-details"
                              onClick={() =>
                                navigate(
                                  `/my-invitations/${invite.id}`
                                )
                              }
                            >
                              Zobacz szczegóły
                            </button>

                            <button
                              className="btn-invite btn-invite-accept"
                              onClick={() =>
                                acceptInvite(invite.id)
                              }
                            >
                              Akceptuję
                            </button>

                            <button
                              className="btn-invite btn-invite-decline"
                              onClick={() =>
                                declineInvite(invite.id)
                              }
                            >
                              Odrzucam
                            </button>

                          </div>
                        </div>
                      </article>
                    ))}

                  </div>
                )}
              </div>
            )}

            {/* EVENTS */}
            {tab === "events" && (
              <div className="panel">
                <h3>Moje wydarzenia</h3>

                {/* {msg && (
                  <div className="alert">
                    {msg}
                  </div>
                )} */}

                {(
                  loadingMyEvents ||
                  loadingRegisteredEvents
                ) ? (
                  <p>
                    Ładujemy Twoje wydarzenia...
                  </p>
                ) : (
                  myEvents.length === 0 &&
                  registeredEvents.length === 0
                ) ? (
                  <p>
                    Nie masz jeszcze żadnych wydarzeń.
                    Po zaakceptowaniu zaproszenia albo
                    samodzielnym zapisaniu się wydarzenie
                    pojawi się tutaj.
                  </p>
                ) : (
                  <>

                    {/* DIRECT REGISTRATIONS */}
                    {registeredEvents.length > 0 && (
                      <section className="profile-events-section">

                        <h4>
                          Wydarzenia, na które się zapisałaś
                        </h4>

                        <div className="invite-grid">

                          {registeredEvents.map((ev) => (
                            <article
                              className="invite-card"
                              key={`registration-${ev.eventId}`}
                            >

                              <div className="invite-image-wrap">
                                <img
                                  src={
                                    ev.imageUrl ||
                                    "/images/single.jpg"
                                  }
                                  alt={ev.title}
                                  className="invite-image"
                                />
                              </div>

                              <div className="invite-content">

                                <span
                                  className={`invite-badge ${
                                    ev.status?.toLowerCase() ||
                                    ""
                                  }`}
                                >
                                  {ev.status === "REGISTERED"
                                    ? "Zapisana"
                                    : ev.status === "CONFIRMED"
                                      ? "Potwierdzone"
                                      : ev.status === "CANCELLED"
                                        ? "Anulowane"
                                        : ev.status}
                                </span>

                                <h4>{ev.title}</h4>

                                <div className="invite-meta">

                                  <span>
                                    {ev.startDateTime
                                      ? new Date(
                                          ev.startDateTime
                                        ).toLocaleString(
                                          "pl-PL",
                                          {
                                            day: "2-digit",
                                            month: "2-digit",
                                            year: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          }
                                        )
                                      : "Termin wkrótce"}
                                  </span>

                                  <span>
                                    {ev.city}
                                  </span>

                                  {ev.place && (
                                    <span>
                                      {ev.place}
                                    </span>
                                  )}

                                </div>

                                {ev.status !== "CANCELLED" && (
                                  <div className="invite-actions">

                                    <button
                                      className="btn-invite btn-invite-details"
                                      onClick={() =>
                                        navigate(
                                          `/events/${ev.eventId}`
                                        )
                                      }
                                    >
                                      Zobacz szczegóły
                                    </button>

                                  </div>
                                )}

                              </div>
                            </article>
                          ))}

                        </div>
                      </section>
                    )}

                    {/* INVITATION EVENTS */}
                    {myEvents.length > 0 && (
                      <section className="profile-events-section">

                        <h4>
                          Wydarzenia z zaproszeń
                        </h4>

                        <div className="invite-grid">

                          {myEvents.map((ev) => (
                            <article
                              className="invite-card"
                              key={`invitation-${ev.id}`}
                            >

                              <div className="invite-image-wrap">
                                <img
                                  src={
                                    ev.imageUrl ||
                                    ev.img ||
                                    "/images/single.jpg"
                                  }
                                  alt={ev.title}
                                  className="invite-image"
                                />
                              </div>

                              <div className="invite-content">

                                <span
                                  className={`invite-badge ${
                                    ev.status?.toLowerCase() ||
                                    ""
                                  }`}
                                >
                                  {ev.status === "ACCEPTED"
                                    ? "Zaakceptowane"
                                    : ev.status === "DECLINED"
                                      ? "Odrzucone"
                                      : ev.status === "EXPIRED"
                                        ? "Czas minął"
                                        : ev.status}
                                </span>

                                <h4>{ev.title}</h4>

                                <div className="invite-meta">
                                  <span>{ev.date}</span>
                                  <span>{ev.time}</span>
                                  <span>
                                    {ev.location ||
                                      ev.city}
                                  </span>
                                </div>

                                {ev.status === "ACCEPTED" && (
                                  <p className="invite-description">
                                    Płatność:{" "}
                                    <b>
                                      {ev.paymentStatus ||
                                        "oczekuje"}
                                    </b>
                                    .
                                    {ev.paymentDeadline && (
                                      <>
                                        {" "}
                                        Termin płatności:{" "}
                                        <b>
                                          {formatDateTime(
                                            ev.paymentDeadline
                                          )}
                                        </b>
                                        .
                                      </>
                                    )}
                                  </p>
                                )}

                                {ev.status !== "DECLINED" &&
                                  ev.status !== "EXPIRED" && (
                                    <div className="invite-actions">

                                      <button
                                        className="btn-invite btn-invite-details"
                                        onClick={() =>
                                          navigate(
                                            `/my-invitations/${ev.id}`
                                          )
                                        }
                                      >
                                        Zobacz szczegóły
                                      </button>

                                    </div>
                                  )}

                              </div>
                            </article>
                          ))}

                        </div>
                      </section>
                    )}

                  </>
                )}
              </div>
            )}

            {/* SETTINGS */}
            {tab === "settings" && (
              <div className="panel">

                <h3>Ustawienia konta</h3>

                {msg && (
                  <div className="alert">
                    {msg}
                  </div>
                )}

                <div className="form-block danger">

                  <h4>Usuwanie konta</h4>

                  <p className="muted">
                    Usunięcie konta spowoduje trwałe
                    skasowanie Twoich danych. Tej operacji
                    nie można cofnąć.
                  </p>

                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={() =>
                      setShowDelete(true)
                    }
                  >
                    Usuń konto
                  </button>

                </div>

                {showDelete && (
                  <div className="form-block">

                    <div className="modal">

                      <h4>
                        Potwierdź usunięcie konta
                      </h4>

                      <p className="muted">
                        Aby potwierdzić, wpisz{" "}
                        <b>USUN</b> i kliknij
                        „Tak, usuń”.
                      </p>

                      <input
                        autoFocus
                        value={confirmText}
                        onChange={(e) =>
                          setConfirmText(
                            e.target.value
                          )
                        }
                        placeholder="USUN"
                      />

                      <div className="modal-actions">

                        <button
                          className="btn btn-primary anuluj usun"
                          type="button"
                          onClick={() =>
                            setShowDelete(false)
                          }
                        >
                          Anuluj
                        </button>

                        <button
                          className="btn btn-primary usun"
                          type="button"
                          disabled={deleting}
                          onClick={handleDeleteAccount}
                        >
                          {deleting
                            ? "Usuwanie..."
                            : "Tak, usuń"}
                        </button>

                      </div>
                    </div>
                  </div>
                )}

              </div>
            )}

          </main>
        </div>
      </div>
    </section>
  );
}